/**
 * The runtime loop (browser side).
 *
 * Macros are pre-authored at design time and bundled (see authored-macros.ts).
 * At runtime there is no strong model and no key. For each request a small
 * in-browser model classifies the intent against the bundled macros; if it
 * routes to one, that macro executes deterministically with the known repo
 * params (GitHub public reads via fetch). No match = honestly "not served".
 *
 * Routing dogfoods the public SDK: @macrokit/runtime `Runtime.chat` over the
 * macro `MacroRegistry`, with the WebLLM adapter as the LLM.
 */
import { Runtime, type ChatResult } from "@macrokit/runtime";
import type { TrajectoryStep } from "./distiller.js";
import type { RouteEnv } from "./env.js";
import type { LearningController } from "./learning.js";
import type { MacroCard, RequestRecord, Session } from "./session.js";

export interface Repo {
  owner: string;
  repo: string;
}

function prompt(repo: Repo, task: string): string {
  return `Repository: ${repo.owner}/${repo.repo}\nTask: ${task}`;
}

/**
 * Pick the macro a classifier reply refers to. Lenient: accepts the exact
 * (normalized) name, a leading list number, an explicit NONE, or a name-token
 * overlap — so a small model's imperfect reply still routes.
 */
function matchMacro(reply: string, cards: MacroCard[]): MacroCard | null {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const r = norm(reply);
  if (!r) return null;
  for (const c of cards) if (r.includes(norm(c.name))) return c;
  const num = reply.trim().match(/^\(?\s*(\d+)/);
  if (num) {
    const i = parseInt(num[1]!, 10) - 1;
    if (cards[i]) return cards[i]!;
  }
  if (/\bnone\b/.test(r)) return null;
  for (const c of cards) {
    const toks = norm(c.name).split(" ").filter((t) => t.length > 3);
    if (toks.some((t) => r.includes(t))) return c;
  }
  return null;
}

/**
 * Weak-model intent classification + deterministic execution.
 *
 * We do NOT use the SDK IntentRouter's tool-calling here: in-browser WebLLM
 * only supports function-calling for a few 7–8B models, not the small models
 * this demo bundles. So routing is done as a constrained TEXT classification
 * (the weak model names the workflow), then the chosen macro is dispatched
 * deterministically with the known repo params — literally "the weak model
 * only does intent classification." Still pure SDK (LLMAdapter + Dispatcher).
 */
type RouteOutcome =
  | { macro: string; ok: true; value: unknown }
  | { macro: string; ok: false; error: string }
  | null;

async function route(session: Session, env: RouteEnv, repo: Repo, task: string): Promise<RouteOutcome> {
  const cards = session.cards.filter((c) => session.distilled.has(c.name));
  if (cards.length === 0) return null;

  const list = cards.map((c) => `- ${c.name}: ${c.intent}`).join("\n");
  const names = cards.map((c) => c.name).join(", ");
  // Worked examples are derived from the ACTUAL loaded cards (a quoted phrase
  // in each intent), never hardcoded — otherwise an example naming a macro
  // that isn't currently registered makes the model route to a phantom or
  // mis-route to a wrong present macro. Plus one NONE example.
  const examples = cards
    .map((c) => {
      const m = c.intent.match(/'([^']+)'/);
      return `Task: ${m ? m[1] : c.name.replace(/_/g, " ")} -> ${c.name}`;
    })
    .join("\n");
  // Small models route far more reliably with an explicit one-token-output
  // instruction + worked examples than with free-form "classify this".
  const system =
    "You map a GitHub maintenance task to exactly one workflow.\n" +
    "Workflows:\n" +
    list +
    "\n\nRules:\n" +
    `- Respond with EXACTLY ONE workflow name from this list: ${names}, NONE\n` +
    "- Output only that single name. No punctuation, no explanation.\n\n" +
    "Examples:\n" +
    examples +
    "\nTask: rename the default branch -> NONE";

  const DEBUG = typeof location !== "undefined" && /[?&]debug=1/.test(location.search);
  let text = "";
  try {
    const res = await env.weak.complete({
      messages: [
        { role: "system", content: system },
        { role: "user", content: `Task: ${task} -> ` },
      ],
      temperature: 0,
      maxTokens: 16,
    });
    text = res.text ?? "";
    if (DEBUG) console.warn("[route] reply:", JSON.stringify(text), "toolCalls:", res.toolCalls?.length ?? 0);
  } catch (e) {
    if (DEBUG) console.warn("[route] complete() threw:", String(e));
    return null;
  }

  const chosen = matchMacro(text, cards);
  if (DEBUG) console.warn("[route] matched:", chosen?.name ?? null);
  if (!chosen) return null;

  // Deterministic execution: supply the known repo params; dispatch via the SDK.
  const rt = new Runtime({ registry: session.distilled, toolSurfaces: { github: env.github } });
  const args: Record<string, unknown> = {};
  for (const p of chosen.params) {
    if (p === "owner") args.owner = repo.owner;
    else if (p === "repo") args.repo = repo.repo;
  }
  const dispatched = await rt.dispatch({ tool: chosen.name, args });
  if (DEBUG) console.warn("[route] dispatch ok:", dispatched.ok, dispatched.ok ? "" : JSON.stringify(dispatched.error));
  if (dispatched.ok) return { macro: chosen.name, ok: true, value: dispatched.value };
  // Routed correctly, but the macro couldn't complete (e.g. the repo has no
  // data this workflow needs — like no open PRs). Surface that honestly rather
  // than pretending no macro matched.
  return { macro: chosen.name, ok: false, error: "the repo may lack the data this workflow needs (e.g. no open pull requests)" };
}

/** Handle one request end to end and append a RequestRecord to the session. */
export async function handleTask(
  session: Session,
  env: RouteEnv,
  repo: Repo,
  task: string,
): Promise<RequestRecord> {
  session.total += 1;
  const id = session.nextRequestId();
  const repoLabel = `${repo.owner}/${repo.repo}`;
  const ts = new Date().toISOString();

  const hit = await route(session, env, repo, task);

  if (hit && hit.ok) {
    session.servedByMacro += 1;
    const card = session.cardFor(hit.macro);
    const value = hit.value as { steps?: Array<{ tool: string; args: Record<string, unknown> }> };
    const rec: RequestRecord = {
      id,
      repo: repoLabel,
      task,
      servedBy: "macro",
      macro: hit.macro,
      trust: card?.trust ?? "auto",
      costLabel: env.macroCostLabel,
      summary: `The in-browser model routed to "${hit.macro}" and it executed ${
        value.steps?.length ?? 0
      } primitive step(s) locally — no cloud model, no key.`,
      steps: (value.steps ?? []).map((s) => ({ tool: s.tool, args: s.args, ok: true })),
      ts,
    };
    session.history.push(rec);
    return rec;
  }

  if (hit && !hit.ok) {
    // Routed correctly but the macro couldn't complete on this repo. Honest:
    // the routing worked; the data didn't cooperate.
    const rec: RequestRecord = {
      id,
      repo: repoLabel,
      task,
      servedBy: "errored",
      macro: hit.macro,
      costLabel: "routed locally · couldn't complete",
      summary: `Routed locally to "${hit.macro}", but it couldn't complete on ${repoLabel} — ${hit.error}.`,
      steps: [],
      ts,
    };
    session.history.push(rec);
    return rec;
  }

  const rec: RequestRecord = {
    id,
    repo: repoLabel,
    task,
    servedBy: "unserved",
    costLabel: "no macro covers this",
    summary:
      "The in-browser model found no bundled macro for this request. New macros " +
      "are authored at design time (see the Macro library — “how these were made”), " +
      "then they too can be served locally.",
    steps: [],
    ts,
  };
  session.history.push(rec);
  return rec;
}

// ===========================================================================
// ACT-2 LEARNING RUNTIME (STUDIO_DISTILLATION_DESIGN.md upgrades 3 & 4)
//
// Confidence-gated routing + strong-model fallback + fail-loud demotion.
// Additive: the demo's handleTask above is untouched. This path routes a
// repeat to a local macro ONLY when intent confidence is high AND args
// validate AND the macro is auto-trusted; every uncertainty falls back to the
// strong model (the safe degrade) and is recorded to feed the next cluster.
// ===========================================================================

const NOVEL_EXTRA = [
  "NOTE: the tools above are LOW-LEVEL PRIMITIVES, not complete workflows. For",
  "this request, call several primitives in sequence to accomplish the task,",
  "then summarize. Operate on the named public repository only. Any label/triage",
  "action must use the *_dryrun primitive — never imply a real write happened.",
].join("\n");

export interface RouteScoreInput {
  bailed: boolean;
  named?: string;
  trust?: "auto" | "candidate";
  distinctCalls: number;
  argsValid: boolean;
}

/**
 * Confidence score in [0,1] for a weak-model routing attempt. We have no
 * logprobs, so confidence is derived from signals: a bail-out (reusing the
 * SDK detector, surfaced via ChatResult.bailOuts) is zero; a single clean call
 * to an auto-trusted macro with valid args is 1.0; ambiguity lowers it.
 */
export function scoreRoute(i: RouteScoreInput): number {
  if (i.bailed) return 0;
  if (!i.named) return 0;
  if (i.trust !== "auto") return 0; // candidates never auto-route
  let s = i.distinctCalls > 1 ? 0.5 : 1.0;
  if (!i.argsValid) s = Math.min(s, 0.5);
  return s;
}

function trajectoryFrom(res: ChatResult): TrajectoryStep[] {
  const steps: TrajectoryStep[] = [];
  for (const d of res.dispatched) {
    if (d.result.ok) steps.push({ tool: d.call.name, args: d.call.args, result: d.result.value });
  }
  return steps;
}

/**
 * Learning runtime: route confidently or fall back to the strong model and
 * learn. Requires a LearningController (cluster buffer + graduation) and an
 * env with a strong adapter for the fallback path.
 */
export async function handleTaskLearning(
  session: Session,
  controller: LearningController,
  env: RouteEnv,
  repo: Repo,
  task: string,
): Promise<RequestRecord> {
  session.total += 1;
  const id = session.nextRequestId();
  const repoLabel = `${repo.owner}/${repo.repo}`;
  const ts = new Date().toISOString();
  const threshold = env.confidenceThreshold ?? 0.7;

  // --- Stage 1: confidence-gated local routing (auto macros only) ---
  const hasAuto = session.cards.some((c) => c.trust === "auto" && session.distilled.has(c.name));
  if (hasAuto) {
    const rt = new Runtime({
      registry: session.distilled,
      llm: env.weak,
      toolSurfaces: { github: env.github },
      maxIterations: 3,
    });
    let res: ChatResult | null = null;
    try {
      res = await rt.chat(prompt(repo, task));
    } catch {
      res = null;
    }
    if (res) {
      const named = res.dispatched.map((d) => d.call.name).find((n) => session.distilled.has(n));
      const card = named ? session.cardFor(named) : undefined;
      const distinctCalls = new Set(res.dispatched.map((d) => d.call.name)).size;

      // Validate args against the macro schema with the known repo params.
      let argsValid = false;
      let parsedArgs: Record<string, unknown> = {};
      if (named && card?.trust === "auto") {
        const macro = session.distilled.lookup(named);
        const args: Record<string, unknown> = {};
        for (const p of card.params) {
          if (p === "owner") args.owner = repo.owner;
          else if (p === "repo") args.repo = repo.repo;
        }
        try {
          parsedArgs = macro!.schema.parse(args) as Record<string, unknown>;
          argsValid = true;
        } catch {
          argsValid = false;
        }
      }

      const score = scoreRoute({
        bailed: res.bailOuts.length > 0,
        ...(named ? { named } : {}),
        ...(card ? { trust: card.trust } : {}),
        distinctCalls,
        argsValid,
      });

      if (score >= threshold && named && argsValid) {
        const dispatched = await rt.dispatch({ tool: named, args: parsedArgs });
        if (dispatched.ok) {
          session.servedByMacro += 1;
          const value = dispatched.value as { steps?: Array<{ tool: string; args: Record<string, unknown> }> };
          const rec: RequestRecord = {
            id,
            repo: repoLabel,
            task,
            servedBy: "macro",
            macro: named,
            trust: "auto",
            costLabel: env.macroCostLabel,
            summary: `Confidently routed to "${named}" (score ${score.toFixed(2)}) and served locally.`,
            steps: (value.steps ?? []).map((s) => ({ tool: s.tool, args: s.args, ok: true })),
            ts,
          };
          session.history.push(rec);
          return rec;
        }
        // FAIL LOUD: the macro errored at runtime — demote it so the next
        // strong-model run re-distills it, then fall through to fallback.
        controller.demote(named);
      }
    }
  }

  // --- Stage 2: strong-model fallback (safe degrade) + learn ---
  if (env.strong) {
    const rt = new Runtime({
      registry: session.primitives,
      llm: env.strong,
      toolSurfaces: { github: env.github },
      systemPromptExtra: NOVEL_EXTRA,
      maxIterations: 8,
    });
    let trajectory: TrajectoryStep[] = [];
    try {
      const res = await rt.chat(prompt(repo, task));
      trajectory = trajectoryFrom(res);
    } catch {
      trajectory = [];
    }

    let graduatedMacro: string | undefined;
    if (trajectory.length > 0) {
      const { signature, ready } = controller.recordRun({
        taskParams: { owner: repo.owner, repo: repo.repo },
        trajectory,
      });
      if (ready) {
        const card = await controller.graduateIfReady(signature);
        if (card) graduatedMacro = card.name;
      }
    }

    const rec: RequestRecord = {
      id,
      repo: repoLabel,
      task,
      servedBy: "claude",
      costLabel: "authored by the strong model · recorded for distillation",
      summary: graduatedMacro
        ? `Strong model handled it; this workflow has now recurred enough to distill "${graduatedMacro}".`
        : "No confident local macro — fell back to the strong model and recorded the run for the next cluster.",
      steps: trajectory.map((s) => ({ tool: s.tool, args: s.args, ok: true })),
      ts,
      ...(graduatedMacro ? { graduatedMacro } : {}),
    };
    session.history.push(rec);
    return rec;
  }

  const rec: RequestRecord = {
    id,
    repo: repoLabel,
    task,
    servedBy: "unserved",
    costLabel: "no confident macro; no strong-model fallback configured",
    summary: "No confident local macro and no strong-model fallback available.",
    steps: [],
    ts,
  };
  session.history.push(rec);
  return rec;
}
